import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, Row, Col, Table, Tag, Typography, Statistic, Space, Alert, notification, Button, Input, Form } from 'antd';
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  FireOutlined,
  TeamOutlined,
  LockOutlined,
  WarningOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone
} from '@ant-design/icons';
import axios from 'axios';
import './TVDashboard.css';

const { Title, Text } = Typography;

// Configure notification placement for TV display
notification.config({
  placement: 'topRight',
  top: 100,
  duration: 8,
});

interface TicketStats {
  total: number;
  new: number;
  open: number;
  pending: number;
  solved: number;
  high: number;
  medium: number;
  low: number;
}

interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  priority: string;
  status: string;
  business_name: string;
  assigned_to_name: string;
  created_at: string;
}

interface AgentWorkload {
  agent_name: string;
  first_name: string;
  open_tickets: number;
  solved_tickets: number;
  total_today: number;
}

const TVDashboard: React.FC = () => {
  const [password, setPassword] = useState<string>('');
  const [passwordInput, setPasswordInput] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [stats, setStats] = useState<TicketStats>({
    total: 0, new: 0, open: 0, pending: 0, solved: 0,
    high: 0, medium: 0, low: 0
  });
  const [recentTickets, setRecentTickets] = useState<Ticket[]>([]);
  const [agentWorkload, setAgentWorkload] = useState<AgentWorkload[]>([]);
  const [urgentOpenTickets, setUrgentOpenTickets] = useState<Ticket[]>([]);
  const [lastTicketCount, setLastTicketCount] = useState(0);
  const [lastStats, setLastStats] = useState<TicketStats>({ total: 0, new: 0, open: 0, pending: 0, solved: 0, high: 0, medium: 0, low: 0 });
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [activityMessages, setActivityMessages] = useState<string[]>([]);
  const [announcement, setAnnouncement] = useState<string>('');
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeZones, setTimeZones] = useState<{ [key: string]: string }>({});
  const [callMeCount, setCallMeCount] = useState(0);
  const [callMeAlertSettings, setCallMeAlertSettings] = useState<{ enabled: boolean; interval: number; tone: string }>({ enabled: true, interval: 5, tone: 'beep' });
  const [hasUserInteractedForSound, setHasUserInteractedForSound] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Unlock audio for playback (must be called from user gesture - click/tap)
  const unlockAudioContext = () => {
    if (audioContextRef.current) {
      if (audioContextRef.current.state === 'closed') {
        audioContextRef.current = null;
      } else {
        audioContextRef.current.resume?.().catch(() => {});
        return;
      }
    }
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      audioContextRef.current = ctx;
      ctx.resume?.().catch(() => {});
    } catch (e) {
      console.warn('[TVDashboard] Could not create AudioContext:', e);
    }
  };

  // Get base URL - ensure we use the correct protocol
  const getBaseURL = () => {
    // Always use the current origin (which should be HTTPS in production)
    // The IIS web.config will proxy /api/* requests to the backend
    const origin = window.location.origin;
    console.log('[TVDashboard] Using base URL:', origin);
    return origin;
  };

  // Create simple axios instance with password
  const createApiClient = (authPassword: string) => {
    const client = axios.create({
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // Add password header to all requests
    client.interceptors.request.use((config) => {
      if (authPassword) {
        config.headers['X-TV-Dashboard-Password'] = authPassword;
      }
      
      // Ensure URL is absolute
      if (config.url) {
        // If already absolute, use as-is
        if (config.url.startsWith('http://') || config.url.startsWith('https://')) {
          // Already absolute, do nothing
        } else if (config.url.startsWith('/')) {
          // Absolute path - prepend base URL
          config.url = getBaseURL() + config.url;
        } else {
          // Relative path - prepend base URL and /
          config.url = getBaseURL() + '/' + config.url;
        }
      }
      
      console.log('[TVDashboard] Request URL:', config.url);
      console.log('[TVDashboard] Request method:', config.method);
      console.log('[TVDashboard] Request headers:', Object.keys(config.headers || {}));
      
      return config;
    }, (error) => {
      console.error('[TVDashboard] Request interceptor error:', error);
      return Promise.reject(error);
    });
    
    // Add response interceptor to log errors
    client.interceptors.response.use(
      (response) => {
        console.log('[TVDashboard] Response received:', response.status, response.config.url);
        return response;
      },
      (error) => {
        console.error('[TVDashboard] Response error:', {
          url: error.config?.url,
          status: error.response?.status,
          statusText: error.response?.statusText,
          message: error.message
        });
        return Promise.reject(error);
      }
    );
    
    return client;
  };

  // Set page title
  useEffect(() => {
    document.title = 'Vitreous Support - TV Dashboard';
    return () => {
      // Reset to default title when component unmounts
      document.title = 'Vitreous Support - Support Ticket Platform';
    };
  }, []);

  // Load password from localStorage on mount and restore session
  useEffect(() => {
    const savedPassword = localStorage.getItem('tv_dashboard_password');
    if (savedPassword) {
      console.log('[TVDashboard] Restoring session from localStorage');
      setPassword(savedPassword);
      setPasswordInput(savedPassword);
      // Optimistically set as authorized - will be verified on first API call
      setIsAuthorized(true);
    }
  }, []);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      
      // Calculate time zones
      const zones: { [key: string]: string } = {};
      zones['EST'] = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      zones['CST'] = new Date(now.toLocaleString('en-US', { timeZone: 'America/Chicago' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      zones['PST'] = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      zones['IST'] = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      zones['PHT'] = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Manila' })).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setTimeZones(zones);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Handle password submission
  const handlePasswordSubmit = async (values: { password?: string }) => {
    unlockAudioContext(); // Unlock audio while we still have user gesture
    setHasUserInteractedForSound(true);
    // Ant Design Form passes values object, but we're using controlled input
    // So we use passwordInput from state, but also check values.password as fallback
    const passwordToUse = passwordInput.trim() || values.password?.trim() || '';
    
    console.log('[TVDashboard] Password submit triggered');
    console.log('[TVDashboard] passwordInput:', passwordInput);
    console.log('[TVDashboard] values.password:', values.password);
    console.log('[TVDashboard] passwordToUse:', passwordToUse);
    
    if (!passwordToUse) {
      setAuthError('Please enter a password');
      return;
    }

    setIsVerifying(true);
    setAuthError('');

    try {
      const url = `${getBaseURL()}/api/auth/tv-dashboard-login`;
      console.log('[TVDashboard] Attempting login to:', url);
      console.log('[TVDashboard] Sending password (length):', passwordToUse.length);
      
      const response = await axios.post(url, {
        password: passwordToUse
      });
      
      console.log('[TVDashboard] Login response:', response.data);
      
      if (response.data.success) {
        console.log('[TVDashboard] Login successful, storing password');
        localStorage.setItem('tv_dashboard_password', passwordToUse);
        setPassword(passwordToUse);
        setIsAuthorized(true);
        setAuthError('');
      } else {
        console.log('[TVDashboard] Login failed - success flag is false');
        setAuthError('Invalid password. Please check and try again.');
        setIsAuthorized(false);
      }
    } catch (error: any) {
      console.error('[TVDashboard] Password verification failed:', error);
      console.error('[TVDashboard] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      if (error.response?.status === 401) {
        setAuthError(error.response?.data?.error || 'Invalid password. Please check and try again.');
      } else if (error.response?.status === 404) {
        setAuthError('Login endpoint not found. Please contact administrator.');
      } else {
        setAuthError(error.response?.data?.error || 'Failed to connect to server. Please check your connection.');
      }
      setIsAuthorized(false);
    } finally {
      setIsVerifying(false);
    }
  };

  // Verify password in background (non-blocking) - only if we have a password but aren't authorized
  useEffect(() => {
    const verifyPassword = async () => {
      // Only verify if we have a password but aren't authorized yet
      // This handles the case where password was just set
      if (!password || isAuthorized) return;

      console.log('[TVDashboard] Verifying password in background...');
      try {
        const response = await axios.post('/api/auth/tv-dashboard-login', {
          password: password
        });
        
        if (response.data.success) {
          console.log('[TVDashboard] Background verification successful');
          setIsAuthorized(true);
          setAuthError('');
        } else {
          console.log('[TVDashboard] Background verification failed - success flag false');
          // Don't clear everything, just mark as unauthorized
          setIsAuthorized(false);
        }
      } catch (error: any) {
        console.error('[TVDashboard] Background password verification failed:', error);
        // Don't clear on background verification failure - let the first API call handle it
        setIsAuthorized(false);
      }
    };

    verifyPassword();
  }, [password, isAuthorized]);

  // Handle logout/clear password
  const handleClearPassword = () => {
    localStorage.removeItem('tv_dashboard_password');
    setPassword('');
    setPasswordInput('');
    setIsAuthorized(false);
    setAuthError('');
  };

  // Play notification sound
  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(e => {
        console.log('Audio play failed, using beep:', e);
        // Fallback: create beep using Web Audio API
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      });
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    if (!isAuthorized || !password) return;

    try {
      console.log('[TVDashboard] Fetching dashboard data...');
      const baseURL = getBaseURL();
      console.log('[TVDashboard] Base URL:', baseURL);
      
      // Use fetch directly to avoid axios interceptor issues
      const statsUrl = `${baseURL}/api/dashboard/stats`;
      console.log('[TVDashboard] Fetching stats from:', statsUrl);
      
      const statsResponse = await fetch(statsUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-TV-Dashboard-Password': password
        },
        credentials: 'same-origin'
      });
      
      console.log('[TVDashboard] Stats response status:', statsResponse.status);
      
      if (!statsResponse.ok) {
        throw new Error(`Stats request failed: ${statsResponse.status} ${statsResponse.statusText}`);
      }
      
      const statsData = await statsResponse.json();
      console.log('[TVDashboard] Stats data:', statsData);
      const newStats = statsData || {};
      
      // Continue with api client for other requests
      const api = createApiClient(password);

      // Use stats from dashboard/stats endpoint directly (it has all the data we need)
      // The stats endpoint already provides: total, new, open, pending, solved, high, medium, low
      const updatedStats = {
        total: Number(newStats.total || 0),
        new: Number(newStats.new || 0),
        open: Number(newStats.open || 0),
        pending: Number(newStats.pending || 0),
        solved: Number(newStats.solved || 0),
        high: Number(newStats.high || 0),
        medium: Number(newStats.medium || 0),
        low: Number(newStats.low || 0)
      };
      
      // Track changes for animations
      setLastStats(updatedStats);
      setStats(updatedStats);
      setLastUpdateTime(new Date());

      // Try to fetch overall counts (may fail if token doesn't have permission)
      try {
        const [openRes, pendingRes, allRes] = await Promise.all([
          api.get('/api/tickets', { params: { status: 'open', limit: 1 } }),
          api.get('/api/tickets', { params: { status: 'pending', limit: 1 } }),
          api.get('/api/tickets', { params: { limit: 1 } })
        ]);
        console.log('[TVDashboard] Tickets counts:', {
          open: openRes.data?.total,
          pending: pendingRes.data?.total,
          all: allRes.data?.total
        });
        // Only update stats if tickets endpoint returns valid data
        // Use nullish coalescing to prefer tickets endpoint data, but fallback to stats endpoint data
        // This ensures we don't overwrite good data with zeros
        const openTotal = openRes.data?.total !== undefined ? Number(openRes.data.total) : newStats.open;
        const pendingTotal = pendingRes.data?.total !== undefined ? Number(pendingRes.data.total) : newStats.pending;
        const allTotal = allRes.data?.total !== undefined ? Number(allRes.data.total) : newStats.total;
        
        // Only update if we got meaningful data (not all zeros from failed calls)
        if (openTotal > 0 || pendingTotal > 0 || allTotal > 0 || 
            (openRes.data?.total === 0 && pendingRes.data?.total === 0 && allRes.data?.total === 0)) {
          // Only update if tickets endpoint explicitly returned 0 for all (meaning no tickets)
          // OR if we got positive values
          setStats(prev => ({
            ...prev,
            open: openTotal,
            pending: pendingTotal,
            total: allTotal
          }));
        }
      } catch (e: any) {
        console.warn('[TVDashboard] Could not fetch ticket counts, using stats endpoint data:', e.response?.status, e.message);
        // Use stats from dashboard/stats endpoint - already set above
      }

      // Fetch Call Me queue count (TV password auth via optionalAuth)
      try {
        const callMeRes = await api.get('/api/call-me/count');
        setCallMeCount(Number(callMeRes.data?.count ?? 0));
      } catch (e: any) {
        console.warn('[TVDashboard] Could not fetch call me count:', e.response?.status, e.message);
        setCallMeCount(0);
      }

      // Check for new tickets and play sound
      const currentTotal = Number(newStats.total || 0);
      if (lastTicketCount > 0 && currentTotal > lastTicketCount) {
        playNotificationSound();
        const newTicketCount = currentTotal - lastTicketCount;
        notification.warning({
          message: '🔔 New Ticket Alert!',
          description: `${newTicketCount} new ticket${newTicketCount > 1 ? 's' : ''} created`,
          icon: <FireOutlined style={{ color: '#ff4d4f' }} />,
          style: {
            backgroundColor: '#fff7e6',
            borderLeft: '4px solid #ff4d4f'
          }
        });
      }
      setLastTicketCount(currentTotal);

      // Fetch high priority tickets first (open and pending)
      let highPriorityTickets: Ticket[] = [];
      try {
        const highRes = await api.get('/api/tickets', {
          params: { 
            priority: 'high',
            limit: 100
          }
        });
        console.log('[TVDashboard] High priority tickets:', highRes.data?.tickets?.length || 0);
        highPriorityTickets = (highRes.data?.tickets || [])
          .filter((t: any) => ['new', 'open', 'pending'].includes(t.status));
        setUrgentOpenTickets(highPriorityTickets);
      } catch (e: any) {
        console.warn('[TVDashboard] Could not fetch high priority tickets:', e.response?.status, e.message);
        setUrgentOpenTickets([]);
      }

      // Fetch recent active tickets (new, open, pending) separately to ensure we get all active tickets
      try {
        // Fetch tickets for each active status separately to get all active tickets
        const [newTicketsRes, openTicketsRes, pendingTicketsRes] = await Promise.all([
          api.get('/api/tickets', {
            params: { 
              status: 'new',
              limit: 100
            }
          }).catch(() => ({ data: { tickets: [], total: 0 } })),
          api.get('/api/tickets', {
            params: { 
              status: 'open',
              limit: 100
            }
          }).catch(() => ({ data: { tickets: [], total: 0 } })),
          api.get('/api/tickets', {
            params: { 
              status: 'pending',
              limit: 100
            }
          }).catch(() => ({ data: { tickets: [], total: 0 } }))
        ]);
        
        // Combine all active tickets
        const allActiveTickets = [
          ...(newTicketsRes.data?.tickets || []),
          ...(openTicketsRes.data?.tickets || []),
          ...(pendingTicketsRes.data?.tickets || [])
        ];
        
        console.log('[TVDashboard] Active tickets:', {
          new: newTicketsRes.data?.tickets?.length || 0,
          open: openTicketsRes.data?.tickets?.length || 0,
          pending: pendingTicketsRes.data?.tickets?.length || 0,
          total: allActiveTickets.length
        });
        
        // Create a map of high priority ticket IDs to avoid duplicates
        const highPriorityIds = new Set(highPriorityTickets.map((t: any) => t.id));
        
        // Get non-high priority tickets
        const otherTickets = allActiveTickets.filter((t: any) => !highPriorityIds.has(t.id));
        
        // Combine: high priority tickets first, then other tickets
        // Sort high priority by created_at desc, then other tickets by created_at desc
        const sortedHighPriority = [...highPriorityTickets].sort((a: any, b: any) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateB - dateA; // Most recent first
        });
        
        const sortedOtherTickets = otherTickets.sort((a: any, b: any) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateB - dateA; // Most recent first
        });
        
        // Combine and limit to 10 total
        const combinedTickets = [...sortedHighPriority, ...sortedOtherTickets].slice(0, 10);
        setRecentTickets(combinedTickets);
      } catch (e: any) {
        console.warn('[TVDashboard] Could not fetch recent tickets:', e.response?.status, e.message);
        // If recent tickets fetch fails, use high priority tickets only
        setRecentTickets(highPriorityTickets.slice(0, 10));
      }

      // Fetch agent workload
      try {
        const agentsRes = await api.get('/api/dashboard/agent-workload');
        console.log('[TVDashboard] Agent workload:', agentsRes.data?.agents?.length || 0);
        setAgentWorkload(agentsRes.data?.agents || []);
      } catch (e: any) {
        console.warn('[TVDashboard] Could not fetch agent workload:', e.response?.status, e.message);
        setAgentWorkload([]);
      }

      console.log('[TVDashboard] Data fetch completed successfully');
    } catch (error: any) {
      console.error('[TVDashboard] Failed to fetch dashboard data:', error);
      console.error('[TVDashboard] Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      if (error.response?.status === 401) {
        // Only clear and show error if we're actually unauthorized
        // This handles the case where password was changed or is invalid
        console.log('[TVDashboard] 401 Unauthorized - password may be invalid');
        setIsAuthorized(false);
        setAuthError('Password invalid or expired. Please enter the correct TV Dashboard password.');
        // Clear invalid password from localStorage
        localStorage.removeItem('tv_dashboard_password');
        setPassword('');
        setPasswordInput('');
      }
    }
  };

  // Fast Call Me polling for near-realtime queue/sound updates on TV Dashboard
  const fetchCallMeCountFast = async () => {
    if (!isAuthorized || !password) return;
    try {
      const res = await fetch(`${getBaseURL()}/api/call-me/count`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-TV-Dashboard-Password': password
        },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!res.ok) return;
      const data = await res.json();
      const nextCount = Number(data?.count ?? 0);
      setCallMeCount(nextCount);
    } catch (e) {
      console.warn('[TVDashboard] Fast call me poll failed:', e);
    }
  };

  // Fetch announcement
  useEffect(() => {
    if (!isAuthorized || !password) return;

    const fetchAnnouncement = async () => {
      try {
        const baseURL = getBaseURL();
        const response = await fetch(`${baseURL}/api/settings/tv-dashboard-announcement`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-TV-Dashboard-Password': password
          },
          credentials: 'same-origin'
        });

        if (response.ok) {
          const data = await response.json();
          setAnnouncement(data.announcement || '');
        }
      } catch (error) {
        console.warn('[TVDashboard] Could not fetch announcement:', error);
      }
    };

    fetchAnnouncement();

    const fetchCallMeAlertSettings = async () => {
      try {
        const res = await fetch(`${getBaseURL()}/api/settings/tv-dashboard-call-me-alert`, {
          headers: { 'X-TV-Dashboard-Password': password },
          credentials: 'same-origin'
        });
        if (res.ok) {
          const data = await res.json();
          const tones = ['beep', 'chime', 'alert', 'soft', 'double'];
          const tone = data.tone && tones.includes(data.tone) ? data.tone : 'beep';
          setCallMeAlertSettings({
            enabled: data.enabled !== false,
            interval: Math.max(5, Math.min(60, data.interval || 5)),
            tone
          });
        }
      } catch (e) {
        console.warn('[TVDashboard] Could not fetch Call Me alert settings:', e);
      }
    };
    fetchCallMeAlertSettings();

    const announcementInterval = setInterval(fetchAnnouncement, 30000);
    const alertSettingsInterval = setInterval(fetchCallMeAlertSettings, 60000);

    return () => {
      clearInterval(announcementInterval);
      clearInterval(alertSettingsInterval);
    };
  }, [isAuthorized, password]);

  // Create AudioContext when dashboard is shown (starts suspended; resume on user click)
  useEffect(() => {
    if (!isAuthorized || audioContextRef.current) return;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (Ctx) {
        audioContextRef.current = new Ctx();
      }
    } catch (e) {
      console.warn('[TVDashboard] Could not create AudioContext:', e);
    }
  }, [isAuthorized]);

  // Play a tone using Web Audio API
  const playTone = (toneId: string, ctx: AudioContext) => {
    const t = ctx.currentTime;
    const playNote = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, start);
      gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
      osc.start(start);
      osc.stop(start + duration);
    };
    switch (toneId) {
      case 'chime':
        playNote(523.25, t, 0.2);
        playNote(659.25, t + 0.15, 0.25);
        break;
      case 'alert':
        playNote(1200, t, 0.4);
        break;
      case 'soft':
        playNote(440, t, 0.35);
        break;
      case 'double':
        playNote(880, t, 0.15);
        playNote(880, t + 0.25, 0.15);
        break;
      default: // beep
        playNote(880, t, 0.3);
    }
  };

  // Call Me alert: play sound every N seconds when queue has items
  // Uses Web Audio API with pre-unlocked context (resumed on user click)
  useEffect(() => {
    if (!callMeAlertSettings.enabled || callMeCount === 0) return;
    const tone = callMeAlertSettings.tone || 'beep';
    const playCallMeAlert = () => {
      const ctx = audioContextRef.current;
      if (!ctx) return;
      if (ctx.state === 'closed') {
        audioContextRef.current = null;
        return;
      }
      (async () => {
        try {
          if (ctx.state === 'suspended') await ctx.resume();
          playTone(tone, ctx);
        } catch (e) {
          console.warn('[TVDashboard] Call Me alert failed:', e);
        }
      })();
    };
    playCallMeAlert();
    const id = setInterval(playCallMeAlert, callMeAlertSettings.interval * 1000);
    return () => clearInterval(id);
  }, [callMeCount, callMeAlertSettings.enabled, callMeAlertSettings.interval, callMeAlertSettings.tone]);

  // Fetch data when authorized
  useEffect(() => {
    if (!isAuthorized || !password) {
      console.log('[TVDashboard] Not fetching data - isAuthorized:', isAuthorized, 'password:', password ? 'exists' : 'missing');
      return;
    }

    console.log('[TVDashboard] Starting data fetch - isAuthorized:', isAuthorized, 'password length:', password.length);
    fetchDashboardData();
    const interval = setInterval(() => {
      console.log('[TVDashboard] Auto-refreshing data...');
      fetchDashboardData();
    }, 60000); // Refresh every 60 seconds (reduces load / 503 risk)

    return () => clearInterval(interval);
  }, [isAuthorized, password]);

  // Keep Call Me queue count highly responsive (independent of full dashboard refresh)
  useEffect(() => {
    if (!isAuthorized || !password) return;

    fetchCallMeCountFast();
    const pollMs = callMeCount > 0 ? 2000 : 5000;
    const callMeInterval = setInterval(fetchCallMeCountFast, pollMs);
    return () => clearInterval(callMeInterval);
  }, [isAuthorized, password, callMeCount]);

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      new: 'blue',
      open: 'orange',
      pending: 'purple',
      solved: 'green',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: any = {
      low: 'green',
      medium: 'orange',
      high: 'red',
    };
    return colors[priority] || 'default';
  };

  // Logo URL with cache busting - memoized to prevent infinite re-renders
  const logoUrl = useMemo(() => {
    const basePath = process.env.PUBLIC_URL || '';
    const cacheBuster = `${Date.now()}_${Math.random()}`;
    return `${basePath}/logo.png?v=${cacheBuster}`.replace('//', '/');
  }, []); // Empty dependency array - only calculate once on mount

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Auth screen
  if (!isAuthorized) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background elements */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
            `,
            opacity: 0.6,
          }}
        />

        {/* Centered Login Card */}
        <div
          style={{
            width: '100%',
            maxWidth: 480,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: 24,
            padding: '56px 48px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            zIndex: 1,
            animation: 'fadeInUp 0.6s ease-out',
          }}
        >
          {/* Logo/Branding at top */}
          <div style={{ marginBottom: 48, textAlign: 'center' }}>
            {logoUrl ? (
              <img
                key={logoUrl}
                src={logoUrl}
                alt="Vitreous Support Logo"
                style={{
                  maxWidth: '320px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '0 auto 32px',
                  transition: 'opacity 0.3s ease',
                }}
                onError={(e) => {
                  console.error('[TVDashboard] Failed to load logo from:', logoUrl);
                }}
                onLoad={() => {
                  console.log('[TVDashboard] Logo loaded successfully from:', logoUrl);
                }}
              />
            ) : (
              <Title level={1} style={{ fontSize: 36, fontWeight: 700, margin: '0 0 32px', color: '#1a1a1a', letterSpacing: '-0.5px' }}>
                Vitreous Support
              </Title>
            )}
            <Title
              level={2}
              style={{
                fontSize: 28,
                fontWeight: 600,
                marginBottom: 8,
                color: '#1a1a1a',
                lineHeight: 1.3,
              }}
            >
              TV Dashboard Access
            </Title>
            <Text type="secondary" style={{ fontSize: 15 }}>
              Enter your password to view the live operations dashboard
            </Text>
          </div>

          {/* Error Alert */}
          {authError && (
            <Alert
              message={authError}
              type="error"
              showIcon
              closable
              onClose={() => setAuthError('')}
              style={{
                marginBottom: 28,
                borderRadius: 12,
                border: '1px solid #ffccc7',
                backgroundColor: '#fff2f0',
              }}
            />
          )}

          {/* Login Form */}
          <Form
            onFinish={handlePasswordSubmit}
            layout="vertical"
            size="large"
            style={{ width: '100%' }}
          >
            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please enter a password' }]}
              style={{ marginBottom: 32 }}
            >
              <Input.Password
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                prefix={<LockOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />}
                placeholder="TV Dashboard Password"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                disabled={isVerifying}
                autoComplete="current-password"
                style={{
                  height: 52,
                  borderRadius: 12,
                  fontSize: 15,
                  border: '1.5px solid #e8e8e8',
                  transition: 'all 0.2s ease',
                  paddingLeft: 44,
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#000';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.05)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e8e8e8';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 24 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isVerifying}
                block
                style={{
                  height: 52,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #20B2AA 0%, #1E88E5 50%, #1E3A8A 100%)',
                  border: 'none',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(30, 58, 138, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 58, 138, 0.3)';
                }}
              >
                {isVerifying ? 'Verifying...' : 'Access Dashboard'}
              </Button>
            </Form.Item>
          </Form>

          {/* Help Text */}
          <Alert
            message="How to get the password:"
            description={
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                <li>Go to Settings → TV Dashboard</li>
                <li>Set a password for TV Dashboard access</li>
                <li>Enter that password here to view the dashboard</li>
              </ul>
            }
            type="info"
            showIcon
            style={{
              borderRadius: 12,
              marginTop: 24,
            }}
          />
        </div>
      </div>
    );
  }

  const handleDashboardClick = () => {
    unlockAudioContext();
    setHasUserInteractedForSound(true);
  };

  // Main dashboard
  return (
    <div className="tv-dashboard" onClick={handleDashboardClick} role="presentation">
      <audio ref={audioRef} preload="auto">
        <source src="/notification.mp3" type="audio/mpeg" />
      </audio>

      {/* Tap to enable sound - shown when Call Me queue has items but user hasn't interacted yet */}
      {callMeCount > 0 && callMeAlertSettings.enabled && !hasUserInteractedForSound && (
        <Alert
          message="Tap anywhere to enable Call Me alert sound"
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ margin: 12, borderRadius: 8 }}
        />
      )}

      {/* Top Header Bar */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="header-logo-container">
            <img
              src={logoUrl}
              alt="Vitreous Support Logo"
              className="header-logo-image"
              onError={(e) => {
                console.error('[TVDashboard] Failed to load logo from:', logoUrl);
                // Fallback to text if logo fails
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const container = target.parentElement;
                if (container) {
                  container.innerHTML = '<div class="header-logo">Support Ticket Statistics</div>';
                }
              }}
              onLoad={() => {
                console.log('[TVDashboard] Logo loaded successfully from:', logoUrl);
              }}
            />
          </div>
        </div>
        <div className="header-center">
        </div>
        <div className="header-right">
          <div className="time-zones">
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/us.png" 
                alt="US" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'US';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">EST</div>
              <div className="timezone-time">{timeZones['EST'] || '--:--:--'}</div>
            </div>
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/us.png" 
                alt="US" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'US';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">CST</div>
              <div className="timezone-time">{timeZones['CST'] || '--:--:--'}</div>
            </div>
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/us.png" 
                alt="US" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'US';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">PST</div>
              <div className="timezone-time">{timeZones['PST'] || '--:--:--'}</div>
            </div>
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/in.png" 
                alt="IN" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'IN';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">IST</div>
              <div className="timezone-time">{timeZones['IST'] || '--:--:--'}</div>
            </div>
            <div className="timezone-item">
              <img 
                src="https://flagcdn.com/w40/ph.png" 
                alt="PH" 
                className="timezone-flag-image"
                loading="lazy"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.dataset.fallbackAdded) {
                    target.dataset.fallbackAdded = 'true';
                    target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'timezone-flag-fallback';
                    fallback.textContent = 'PH';
                    target.parentElement?.insertBefore(fallback, target);
                  }
                }}
              />
              <div className="timezone-label">PHT</div>
              <div className="timezone-time">{timeZones['PHT'] || '--:--:--'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Main KPI Cards Row */}
        <div className="kpi-row">
            {/* Total Active */}
            <div className={`kpi-card total-active ${stats.total > lastStats.total ? 'pulse' : ''}`}>
              <div className="kpi-value">{stats.open + stats.pending + stats.high}</div>
              <div className="kpi-label">Total Active</div>
            </div>

          {/* Call Me */}
          <div className={`kpi-card call-me ${callMeCount > 0 ? 'glow' : ''}`}>
            <div className="kpi-value">{callMeCount}</div>
            <div className="kpi-label">Call Me</div>
            {callMeCount > 0 && <div className="kpi-subtext">Waiting</div>}
          </div>

          {/* Open Tickets */}
          <div className={`kpi-card open ${stats.open > lastStats.open ? 'flash' : ''}`}>
            <div className="kpi-value">{stats.open}</div>
            <div className="kpi-label">Open</div>
            {stats.open > lastStats.open && (
              <div className="kpi-subtext">+{stats.open - lastStats.open} new</div>
            )}
          </div>

          {/* Pending */}
          <div className={`kpi-card pending ${stats.pending > lastStats.pending ? 'pulse' : ''}`}>
            <div className="kpi-value">{stats.pending}</div>
            <div className="kpi-label">Pending</div>

          </div>

          {/* High Priority */}
          <div className={`kpi-card high-priority ${stats.high > 0 ? 'glow' : ''}`}>
            <div className="kpi-value">{stats.high}</div>
            <div className="kpi-label">High Priority</div>
            {stats.high > 0 && <div className="kpi-subtext">Requires attention</div>}
          </div>

          {/* Solved Today */}
          <div className={`kpi-card solved ${stats.solved > lastStats.solved ? 'checkmark' : ''}`}>
            <div className="kpi-value">{stats.solved}</div>
            <div className="kpi-label">Solved Today</div>
            {stats.solved > lastStats.solved && (
              <div className="kpi-subtext">+{stats.solved - lastStats.solved} resolved</div>
            )}
          </div>
        </div>

        {/* Recent Tickets Table */}
        <div className="tickets-section">
          <div className="tickets-card">
            <div className="tickets-card-header">
              <h2 className="tickets-card-title">
                <CheckCircleOutlined /> Recent Active Tickets
              </h2>
            </div>
            {recentTickets.length === 0 ? (
              <div className="no-data-message">
                <div className="no-data-icon">💤</div>
                <div>No active tickets — all systems stable</div>
              </div>
            ) : (
              <Table
                dataSource={recentTickets.slice(0, 10)}
                rowKey="id"
                pagination={false}
                className="tickets-table"
                rowClassName={(record: any) => {
                  return record.priority === 'high' ? 'high-priority-row' : '';
                }}
                onRow={(record) => ({
                  onMouseEnter: (e) => {
                    const target = e.currentTarget as HTMLElement;
                    const isHighPriority = record.priority === 'high';
                    if (isHighPriority) {
                      target.style.backgroundColor = 'rgba(255, 77, 79, 0.3)';
                      target.style.borderLeft = '4px solid rgba(255, 77, 79, 1)';
                    } else {
                      target.style.backgroundColor = 'rgba(24, 144, 255, 0.7)';
                      target.style.borderLeft = '4px solid rgba(64, 169, 255, 1)';
                    }
                    const cells = target.querySelectorAll('td');
                    cells.forEach(cell => {
                      cell.style.backgroundColor = 'transparent';
                      cell.style.color = '#ffffff';
                      // Handle all nested elements including Ant Design Typography components
                      const allElements = cell.querySelectorAll('*');
                      allElements.forEach(el => {
                        const htmlEl = el as HTMLElement;
                        htmlEl.style.color = '#ffffff';
                        // Also set style attribute directly
                        htmlEl.setAttribute('style', htmlEl.getAttribute('style') + '; color: #ffffff !important;');
                      });
                    });
                  },
                  onMouseLeave: (e) => {
                    const target = e.currentTarget as HTMLElement;
                    // Reset row background and border
                    target.style.backgroundColor = '';
                    target.style.borderLeft = '';
                    
                    const cells = target.querySelectorAll('td');
                    cells.forEach(cell => {
                      // Reset cell background but keep text white
                      cell.style.backgroundColor = '';
                      cell.style.color = '#ffffff';
                      
                      // Ensure all text elements remain white
                      const allElements = cell.querySelectorAll('*');
                      allElements.forEach(el => {
                        const htmlEl = el as HTMLElement;
                        // Always maintain white color for text elements
                        htmlEl.style.color = '#ffffff';
                      });
                    });
                  }
                })}
                columns={[
                  {
                    title: 'Ticket #',
                    dataIndex: 'ticket_number',
                    key: 'ticket_number',
                    width: 150,
                    render: (text: string) => <Text strong style={{ fontSize: '24px', color: '#ffffff' }}>{text}</Text>
                  },
                  {
                    title: 'Subject',
                    dataIndex: 'subject',
                    key: 'subject',
                    ellipsis: true,
                    render: (text: string) => <span style={{ fontSize: '24px', color: '#ffffff' }}>{text}</span>
                  },
                  {
                    title: 'Business',
                    dataIndex: 'business_name',
                    key: 'business_name',
                    width: 200,
                    ellipsis: true,
                    render: (text: string) => <span style={{ fontSize: '24px', color: '#ffffff' }}>{text || 'N/A'}</span>
                  },
                  {
                    title: 'Priority',
                    dataIndex: 'priority',
                    key: 'priority',
                    width: 150,
                    render: (priority: string) => (
                      <span style={{ fontSize: '24px', display: 'flex', alignItems: 'center', color: '#ffffff' }}>
                        <span className={`priority-dot ${priority?.toLowerCase() || 'low'}`}></span>
                        {priority?.toUpperCase() || 'LOW'}
                      </span>
                    )
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    width: 150,
                    render: (status: string) => (
                      <span style={{ fontSize: '24px', display: 'flex', alignItems: 'center', color: '#ffffff' }}>
                        <span className={`status-dot ${status?.toLowerCase() || 'open'}`}></span>
                        {status?.toUpperCase() || 'OPEN'}
                      </span>
                    )
                  },
                  {
                    title: 'Assigned To',
                    dataIndex: 'assigned_to_name',
                    key: 'assigned_to_name',
                    width: 200,
                    render: (name: string) => <span style={{ fontSize: '24px', color: '#ffffff' }}>{name || 'Unassigned'}</span>
                  },
                  {
                    title: 'Created',
                    dataIndex: 'created_at',
                    key: 'created_at',
                    width: 150,
                    render: (date: string) => <span style={{ fontSize: '24px', color: '#ffffff' }}>{formatTimeAgo(date)}</span>
                  }
                ]}
              />
            )}
          </div>
        </div>

        {/* Last Updated */}
        <div className="last-updated" style={{ textAlign: 'center', marginTop: '24px', marginBottom: '24px' }}>
          Last updated {Math.floor((new Date().getTime() - lastUpdateTime.getTime()) / 1000)} seconds ago
        </div>

        {/* Logout Button */}
        <div style={{ textAlign: 'center', marginBottom: '180px' }}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={handleClearPassword}
            style={{ color: '#fff', fontSize: '18px', fontWeight: 600, padding: '8px 24px' }}
          >
            Logout
          </Button>
        </div>
      </div>

      {/* Announcement Banner (Bottom Strip) - Show first if exists */}
      {announcement && announcement.trim().length > 0 && (
        <div 
          className="activity-banner announcement-banner"
          style={{ bottom: activityMessages.length > 0 ? '70px' : '30px' }}
        >
          <div className="activity-ticker announcement-ticker">
            {/* Render announcement multiple times for seamless loop */}
            {[announcement, announcement, announcement, announcement].map((msg, idx) => (
              <div key={`announcement-${idx}`} className="activity-item announcement-item">
                <span>{msg}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Banner (Bottom Strip) */}
      {activityMessages.length > 0 && (
        <div className="activity-banner">
          <div className="activity-ticker">
            {/* Render messages multiple times for seamless infinite loop */}
            {[...activityMessages, ...activityMessages, ...activityMessages].map((msg, idx) => (
              <div key={`activity-msg-${idx}`} className="activity-item">
                <span className="activity-icon">🔔</span>
                <span>{msg}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default TVDashboard;
